import axios from 'axios'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Spinner} from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import CurrencyInput from 'react-currency-input-field'
import {useTranslation} from 'react-i18next'
import 'react-tabs/style/react-tabs.css'
import swal from 'sweetalert'
import {KTSVG} from '../../../../_metronic/helpers'
import {editPPN} from '../../../../api/modules/master-data/master'
import Pagination from '../../component/Pagination'
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks'
import {
  filterSelector,
  resetFilterPPN,
  setFilterPPN,
  setIsFetchData,
} from '../../../../redux/slices/filterSlices'
import {fetchPPN, headers} from '../../../../services/fetcher'
import {objToParams} from '../../../../utils/objToParams'
import {SelectPageSize} from '../../../../components/SelectPageSize/SelectPageSize'
import {RowFilter} from './RowFilter'
import {useRowTable} from '../../../../hooks/useRowTable'
import * as TableUI from '../../../../components/Table/Table'
import {StyledCard} from '../../../../components/StyledCard'
import {StyledButtonGroups} from '../../../../components/StyledButtonGroups'
import dateformat from 'src/utils/dateformat'

type Props = {
  className: string
}

const TablePPN: React.FC<Props> = () => {
  const {filterPPN} = useAppSelector(filterSelector)
  const dispatch = useAppDispatch()
  const [allPPN, setAllPPN] = useState<any>([])
  const [ppn, setPPN] = useState(0)
  const [page, setPage] = useState(0)
  const [numberURL, setNumberURL] = useState([])
  const [createdAt, setCreatedAt] = useState('')
  const [updatedAt, setUpdatedAt] = useState('')
  const [showEdit, setShowEdit] = useState(false)
  const [language, setLanguage] = useState<any>()
  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const {t} = useTranslation()

  const rowNumber = useMemo(() => {
    const per = filterPPN?.per_page ?? 5
    return page * per - per
  }, [filterPPN?.per_page, page])

  const onFetchPPN = useCallback(() => {
    setIsLoading(true)
    setIsError(false)
    fetchPPN(filterPPN)
      .then((res) => {
        setAllPPN(res.data.data.data)
        setPage(res.data.data.current_page)
        setNumberURL(res.data.data.links)
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setIsLoading(false)
        dispatch(setIsFetchData(false))
      })
  }, [filterPPN])

  const paginateNumber = async (url: string) => {
    if (!url) return
    try {
      setIsLoading(true)

      const token = localStorage.getItem('token') as string
      const endpoint = `${url}&${objToParams(filterPPN)}`
      const res = await axios.get(endpoint, {
        headers: {...headers, Authorization: `Bearer ${token}`},
      })
      const data = res.data.data

      setAllPPN(data.data)
      setPage(data.current_page)
      setNumberURL(data.links)
    } catch (error) {
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = async () => {
    setBtnLoading(true)
    swal({
      title: `${t('Are you sure?')}`,
      icon: 'warning',
      buttons: [`${t('Tidak')}`, `${t('Ya')}`],
      dangerMode: true,
    })
      .then(async (isedit) => {
        if (isedit) {
          await editPPN({ppn: ppn})
            .then((res: any) => {
              swal({
                title: t('Master PPN Berhasil di Ubah'),
                icon: 'success',
              }).then((yes) => {
                if (yes) {
                  setShowEdit(false)
                  onFetchPPN()
                }
              })
            })
            .catch((error) => {
              swal({
                closeOnClickOutside: false,
                title: t(error.response.data.message),
                icon: 'error',
              })
            })
        }
      })
      .finally(() => setBtnLoading(false))
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const getCookie = (name: string) => {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) return parts.pop()?.split('~#~').shift()
      }
      let lang = getCookie('i18next')
      setLanguage(lang)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const handlePageSizeChange = (value: number | null | undefined) => {
    dispatch(setFilterPPN({...filterPPN, per_page: value ?? 5}))
  }

  const handleResetTable = () => {
    dispatch(resetFilterPPN())
  }

  useEffect(() => {
    return () => {
      handleResetTable()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetcher = setTimeout(onFetchPPN, 1000)
    return () => clearTimeout(fetcher)
  }, [onFetchPPN])

  const {RowEmptyData, RowErrorFetch, RowLoader} = useRowTable({
    colSpan: 7,
    onReloadTable: handleResetTable,
  })

  return (
    <StyledCard>
      <StyledButtonGroups
        leftContent={
          <SelectPageSize
            initialValue={filterPPN?.per_page ?? 5}
            onChange={(value: any) => handlePageSizeChange(value?.value)}
          />
        }
      />

      <TableUI.TableWrapper>
        <TableUI.Table>
          <TableUI.THead>
            <tr>
              <th>{t('TablePPN.No')}</th>
              <th>PPN</th>
              <th>{t('TablePPN.created_at')}</th>
              <th>{t('TablePPN.updated_at')}</th>
              <th>{t('TablePPN.action')}</th>
            </tr>
          </TableUI.THead>
          <TableUI.TBody>
            <RowFilter />

            {isLoading ? (
              <RowLoader />
            ) : isError ? (
              <RowErrorFetch />
            ) : allPPN.length === 0 ? (
              <RowEmptyData />
            ) : (
              allPPN.map((item: any, index: number) => (
                <tr key={item?.id ?? index}>
                  <td>
                    <div className='py-3'>{index + 1 + rowNumber}</div>
                  </td>
                  <td>
                    <div className='py-3'>{item.ppn}%</div>
                  </td>
                  <td>
                    <div className='py-3'>{dateformat(item.created_at)}</div>
                  </td>
                  <td>
                    <div className='py-3'>{dateformat(item.updated_at)}</div>
                  </td>
                  <td>
                    <button
                      className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                      title={t('ubah')}
                      onClick={() => {
                        setPPN(item.ppn)
                        setCreatedAt(item.created_at)
                        setUpdatedAt(item.updated_at)
                        setShowEdit(true)
                      }}
                    >
                      <KTSVG path='/media/icons/duotune/pencil-fill.svg' className='svg-icon-3' />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </TableUI.TBody>
        </TableUI.Table>
      </TableUI.TableWrapper>

      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        backdrop='static'
        keyboard={false}
        centered
        size='lg'
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('Edit')} PPN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className='mb-5'>
            <Col sm={3}>
              <Form.Label>PPN</Form.Label>
            </Col>
            <Col sm={9}>
              <InputGroup className='mb-3'>
                <CurrencyInput
                  className='form-control form-control-sm'
                  placeholder='PPN'
                  decimalSeparator={language === 'en' ? '.' : ','}
                  groupSeparator={language === 'en' ? ',' : '.'}
                  defaultValue={ppn}
                  onValueChange={(value: any) => {
                    if (value?.replace(/,/g, '.') == undefined) setPPN(0)
                    else setPPN(parseInt(value?.replace(/,/g, '.')))
                  }}
                  decimalsLimit={3}
                />
                <InputGroup.Text>
                  <span className='px-2'>%</span>
                </InputGroup.Text>
              </InputGroup>
            </Col>
          </Row>
          <Row className='mb-5'>
            <Col sm={3}>
              <Form.Label>{t('Created At')}</Form.Label>
            </Col>
            <Col sm={9}>
              <Form.Control
                disabled
                aria-label='Default select example'
                defaultValue={createdAt.slice(0, 10)}
              />
            </Col>
          </Row>
          <Row className='mb-5'>
            <Col sm={3}>
              <Form.Label>{t('Updated At')}</Form.Label>
            </Col>
            <Col sm={9}>
              <Form.Control
                disabled
                aria-label='Default select example'
                defaultValue={updatedAt.slice(0, 10)}
              />
            </Col>
          </Row>
          <div className='d-flex justify-content-end'>
            <Button variant='primary' onClick={() => handleEdit()} disabled={btnLoading}>
              {btnLoading ? (
                <Spinner animation='border' variant='light' size='sm' />
              ) : (
                <>
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  {t('order-kirim')}
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Pagination paginate={paginateNumber} test={numberURL} />
    </StyledCard>
  )
}

export {TablePPN}
